import { onAuthStateChanged } from 'firebase/auth'
import { auth, IS_FIREBASE_ENABLED } from './firebase'
import { OfflineError } from './errors'

/**
 * Firebase restores a persisted session asynchronously, so `auth.currentUser`
 * is null for a short window after page load even when the user is signed in.
 * Any request issued in that window went out with no Authorization header and
 * came back 401 "Missing or invalid authorization header".
 *
 * useAuth hydrates from an IndexedDB snapshot and clears `loading` before
 * Firebase has restored, which is good for first paint but means route guards
 * pass and pages mount and fetch inside that window. The admin overview showed
 * it worst because it fires six requests on mount, but it was never
 * admin-specific: UniversitiesPage carries a bespoke retry keyed on that exact
 * error string, and QuestionPage maps the same 401 to "A tua sessão expirou",
 * which is simply false during a startup race.
 *
 * Gating token retrieval on the first auth-state callback removes the class
 * rather than the symptom.
 *
 * Bounded on purpose. getAuthToken sits in front of every request in the app,
 * so an unbounded await would turn a stalled Firebase into a frozen client.
 * The promise is cached, so at most one call ever pays the timeout; every
 * later call sees an already-settled promise. Resolving early on timeout is
 * safe: the request proceeds exactly as it did before this change.
 */
const AUTH_READY_TIMEOUT_MS = 5_000

let authReadyPromise: Promise<void> | null = null

function waitForAuthReady(): Promise<void> {
  // Nothing to wait for when Firebase is not the auth source.
  if (!IS_FIREBASE_ENABLED || !auth) return Promise.resolve()
  // Already restored: the common case after the first load.
  if (auth.currentUser) return Promise.resolve()

  if (!authReadyPromise) {
    authReadyPromise = new Promise<void>((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        unsubscribe()
        resolve()
      }
      // Fires with null for a signed-out user too, which is the correct
      // moment to stop waiting and fall through to the anonymous token.
      const unsubscribe = onAuthStateChanged(auth!, finish, finish)
      const timer = setTimeout(finish, AUTH_READY_TIMEOUT_MS)
    })
  }
  return authReadyPromise
}

export interface DemoStudent {
  id: string
  name: string
  email: string
  target_university: string
  active_subject: string
  is_admin: boolean
  role?: string                  // 'student' | 'teacher' | 'admin'
  teacher_subject_ids?: string[] // for teachers only
  has_programme?: boolean
}

export interface AuthSession {
  token: string       // "demo_<studentId>"
  student: DemoStudent
}

const SESSION_KEY = 'tutor_session'
const LEGACY_SESSION_KEY = 'acesso_session'
const ANON_SESSION_KEY = 'tutor_anon_session'
const LEGACY_ANON_SESSION_KEY = 'acesso_anon_session'

function getStorage(kind: 'local' | 'session'): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage
  } catch {
    return null
  }
}

function readLocalStorageValue(primaryKey: string, legacyKey?: string): string | null {
  const storage = getStorage('local')
  if (!storage) return null
  const primary = storage.getItem(primaryKey)
  if (primary) return primary
  if (!legacyKey) return null
  const legacy = storage.getItem(legacyKey)
  if (legacy) {
    storage.setItem(primaryKey, legacy)
    storage.removeItem(legacyKey)
  }
  return legacy
}

function readAnonymousStorageValue(): string | null {
  const session = getStorage('session')
  const local = getStorage('local')
  const current = session?.getItem(ANON_SESSION_KEY) ?? null
  if (current) return current

  const primary = local?.getItem(ANON_SESSION_KEY) ?? null
  if (primary) {
    session?.setItem(ANON_SESSION_KEY, primary)
    local?.removeItem(ANON_SESSION_KEY)
    return primary
  }

  const legacy = local?.getItem(LEGACY_ANON_SESSION_KEY) ?? null
  if (legacy) {
    session?.setItem(ANON_SESSION_KEY, legacy)
    local?.removeItem(LEGACY_ANON_SESSION_KEY)
    return legacy
  }

  return null
}

export function getSession(): AuthSession | null {
  if (IS_FIREBASE_ENABLED) return null
  try {
    const raw = readLocalStorageValue(SESSION_KEY, LEGACY_SESSION_KEY)
    return raw ? (JSON.parse(raw) as AuthSession) : null
  } catch {
    return null
  }
}

export function setSession(session: AuthSession): void {
  const storage = getStorage('local')
  storage?.setItem(SESSION_KEY, JSON.stringify(session))
  storage?.removeItem(LEGACY_SESSION_KEY)
}

export function clearSession(): void {
  getStorage('local')?.removeItem(SESSION_KEY)
  getStorage('local')?.removeItem(LEGACY_SESSION_KEY)
  getStorage('local')?.removeItem(ANON_SESSION_KEY)
  getStorage('local')?.removeItem(LEGACY_ANON_SESSION_KEY)
  getStorage('session')?.removeItem(ANON_SESSION_KEY)
}

export interface AnonymousSession {
  token: string
  student_id: string
}

export function getAnonymousSession(): AnonymousSession | null {
  if (!IS_FIREBASE_ENABLED) return null
  try {
    const raw = readAnonymousStorageValue()
    return raw ? (JSON.parse(raw) as AnonymousSession) : null
  } catch {
    return null
  }
}

export function setAnonymousSession(session: AnonymousSession): void {
  getStorage('session')?.setItem(ANON_SESSION_KEY, JSON.stringify(session))
  getStorage('local')?.removeItem(ANON_SESSION_KEY)
  getStorage('local')?.removeItem(LEGACY_ANON_SESSION_KEY)
}

export function clearAnonymousSession(): void {
  getStorage('session')?.removeItem(ANON_SESSION_KEY)
  getStorage('local')?.removeItem(ANON_SESSION_KEY)
  getStorage('local')?.removeItem(LEGACY_ANON_SESSION_KEY)
}

export function getAnonymousToken(): string | null {
  return getAnonymousSession()?.token ?? null
}

export async function revokeAnonymousSession(): Promise<void> {
  const session = getAnonymousSession()
  if (!session) return
  const baseUrl = import.meta.env.VITE_API_URL ?? '/api'
  try {
    await fetch(`${baseUrl}/auth/revoke-anonymous-session`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.token}`,
      },
    })
  } catch {
    // Shared-device reset should still proceed locally if the network is dead.
  } finally {
    clearAnonymousSession()
  }
}

// Normalizes Firebase network failures to OfflineError so the app can switch
// to offline UX instead of surfacing raw SDK errors.
export async function getAuthToken(): Promise<string | null> {
  if (IS_FIREBASE_ENABLED) {
    // Wait for Firebase to restore any persisted session before deciding
    // whether there is a signed-in user. See waitForAuthReady above.
    await waitForAuthReady()
    if (auth?.currentUser) {
      try {
        return await auth.currentUser.getIdToken()
      } catch (err: any) {
        const code = err?.code ?? ''
        const message = err?.message ?? ''
        if (
          code === 'auth/network-request-failed' ||
          /network-request-failed/i.test(message)
        ) {
          throw new OfflineError('firebase:network-request-failed')
        }
        throw err
      }
    }
    return getAnonymousToken()
  }
  return getSession()?.token ?? null
}
